import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Navigate } from 'react-router-dom';

import { So5LeaderboardType } from '@sorare/core/src/__generated__/globalTypes';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { League } from '@sorare/core/src/components/landing/types';
import { FOOTBALL_PLAY } from '@sorare/core/src/constants/__generated__/routes';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { useIsLoggedIn } from '@sorare/core/src/hooks/auth/useIsLoggedIn';

import { WalletLanding } from 'components/landing/WalletLanding';
import { winAssets } from 'pages/assets/assets';

import bgAvif from './assets/hero/bg.avif';
import bg from './assets/hero/bg.webp';
import leftSidePlayersAvif from './assets/hero/players/leftSidePlayers.avif';
import leftSidePlayers from './assets/hero/players/leftSidePlayers.webp';
import rightSidePlayersAvif from './assets/hero/players/rightSidePlayers.avif';
import rightSidePlayers from './assets/hero/players/rightSidePlayers.webp';
import leagueLogo from './assets/league-logo.webp';
import fieldAvif from './assets/play/field.avif';
import field from './assets/play/field.webp';
import lineupAvif from './assets/play/lineup.avif';
import lineup from './assets/play/lineup.webp';
import scoutBgAvif from './assets/scout/background.avif';
import scoutBg from './assets/scout/background.webp';
import playersAvif from './assets/scout/players.avif';
import players from './assets/scout/players.webp';
import social from './assets/social.webp';

const messages = defineMessages({
  metadatasTitle: {
    id: 'Ligue1Landing.metadatas.title',
    defaultMessage: 'The Real Fantasy Ligue 1 Game – Sorare Football',
  },
  metadatasDescription: {
    id: 'Ligue1Landing.metadatas.description',
    defaultMessage:
      "Collect, play and win officially licensed digital cards featuring players from the Ligue 1, the world's best football league on Sorare",
  },
});

const heroAssets = {
  background: {
    avif: bgAvif,
    pictureUrl: bg,
  },
  players: [
    {
      avif: leftSidePlayersAvif,
      pictureUrl: leftSidePlayers,
      height: 430,
      width: 658,
    },
    {
      avif: rightSidePlayersAvif,
      pictureUrl: rightSidePlayers,
      height: 432,
      width: 557,
    },
  ],
};

const scoutAssets = {
  background: {
    avif: scoutBgAvif,
    pictureUrl: scoutBg,
  },
  players: {
    avif: playersAvif,
    pictureUrl: players,
  },
};

const playAssets = {
  background: {
    avif: fieldAvif,
    pictureUrl: field,
  },
  lineup: {
    avif: lineupAvif,
    pictureUrl: lineup,
  },
};

const FootballPage = () => {
  const isLoggedIn = useIsLoggedIn();
  const { formatMessage } = useIntl();
  const { setPageMetadata } = useSeoContext();

  useEffect(
    () =>
      setPageMetadata(formatMessage(messages.metadatasTitle), {
        img: social,
        description: formatMessage(messages.metadatasDescription),
      }),
    [setPageMetadata, formatMessage]
  );

  if (isLoggedIn) {
    return <Navigate to={FOOTBALL_PLAY} replace />;
  }

  return (
    <WalletLanding
      assets={{
        heroAssets,
        scoutAssets,
        playAssets,
        winAssets,
      }}
      leaderboards={[
        So5LeaderboardType.IN_SEASON_FRANCE_LIMITED,
        So5LeaderboardType.IN_SEASON_FRANCE_RARE,
        So5LeaderboardType.IN_SEASON_FRANCE_SUPER_RARE,
      ]}
      leagueLogo={
        <Img src={leagueLogo} alt="Ligue 1" width="240" height="80" />
      }
      league={League.Ligue1}
    />
  );
};

export default FootballPage;
